import React from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  GENERATE_TRANSACTION_RECEIPT,
  GenerateTransactionReceiptData,
  GenerateTransactionReceiptVars,
} from "common/graphql/mutations/GenerateTransactionReceipt";
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridFilterInputSingleSelect,
  GridSortModel,
  getGridDateOperators,
  getGridStringOperators,
  GridActionsCellItem,
  GridActionsColDef,
} from "@mui/x-data-grid-pro";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { Link as RouterLink, NavigateFunction, To, useNavigate, useSearchParams } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { format } from "date-fns";
import startOfMonth from "date-fns/startOfMonth";
import {
  DataTableContainer,
  ACTIONS_COLUMN_ICON,
  ACTIONS_VIEW_ICON_SX,
  FilteredColumnsPanel,
  STANDARD_GRID_PROPS,
  STANDARD_COLUMN_HEADER_TITLE_SX,
} from "../DataTableContainer";
import {
  GET_PAYMENT_TRANSACTIONS,
  GetPaymentTransactionsData,
  GetPaymentTransactionsVars,
  PayerTransactionOrderBy,
  PayerTransactionsFilter,
  USAGE_FEE_FILTER,
  USAGE_FEE_TRANSACTION_TYPES,
  buildPaymentTransactionsWhere,
} from "common/graphql/queries/GetPaymentTransactions";
import { PayerTransactionLogEntry } from "common/graphql/fragments/payerTransactionLogEntryFragment";
import { WalletPaymentPreference } from "common/graphql/fragments/walletFragment";

import {
  parseSortModel,
  serializeSortModel,
  parseFilterModelFromSearchParams,
  filterModelToSearchParams,
  OrderBy,
} from "common/graphql/queryString";
import { formatCurrency } from "common/format";
import LocalLevelError from "@app.automotus.io/components/common/LocalLevelError";
import ManualApplyGridFilterPanel from "@app.automotus.io/components/ManualApplyGridFilterPanel";
import type { Theme } from "@mui/material/styles";
import useIsMobile from "@app.automotus.io/components/hooks/useIsMobile";
import { PayerTableToolbar, PayerTableToolbarProps } from "./PayerTableToolbar";
import { SNACKBAR_MSGS, useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import type { DisputedTransactionRow } from "./disputedTransactions";

const PAGE_SIZE = 1000;
const DEFAULT_SORT = "date+desc";
const PARK_PERIOD_FORMAT = "h:mm a";

const TRANSACTION_TYPE_OPTIONS = ["Usage Fee", "Session Payment", "Wallet Load", "Refund", "Adjustment"] as const;

/**
 * AUT-6162: a chargeback/credit against an earlier transaction (PRO-2210). Its gateway type is
 * null, like a park row's, so it has to be identified by transactionType -- and checked before
 * anything that keys off a null gateway type.
 */
export const isAdjustmentRow = (row: Pick<PayerTransactionLogEntry, "transactionType">): boolean =>
  row.transactionType === "adjustment";

export const transactionTypeLabel = (
  row: Pick<PayerTransactionLogEntry, "transactionType" | "gatewayTransactionType">,
): string => {
  if (isAdjustmentRow(row)) {
    return "Adjustment";
  }
  if (row.gatewayTransactionType === "wallet_refund") {
    return "Refund";
  }
  if (row.gatewayTransactionType === "initial_wallet_load" || row.gatewayTransactionType === "wallet_reload") {
    return "Wallet Load";
  }
  if (row.gatewayTransactionType === "session_payment") {
    return "Session Payment";
  }
  if (row.gatewayTransactionType === "permit") {
    return "Permit";
  }
  return "Usage Fee";
};

/**
 * A park/usage row ("Usage Fee") carries location + session data; gateway transactions and
 * adjustments do not. AUT-6162: an explicit transaction_type allowlist (park, trip_charge), the
 * same definition as USAGE_FEE_FILTER and the monthly charges park aggregate, so a new non-gateway
 * type never silently becomes a usage fee.
 */
export const isParkRow = (row: Pick<PayerTransactionLogEntry, "transactionType">): boolean =>
  (USAGE_FEE_TRANSACTION_TYPES as readonly string[]).includes(row.transactionType);

/** True only for gateway types that load money into the wallet. */
const isWalletDeposit = (row: PayerTransactionLogEntry): boolean =>
  row.gatewayTransactionType === "initial_wallet_load" || row.gatewayTransactionType === "wallet_reload";

/**
 * declined, error, pending, and held_for_review all zero out `price` at the DB layer, so
 * gatewayTransactionStatus is what lets us label each state on its own terms instead of
 * asserting "Declined" for all of them. pending and held_for_review both mean "not resolved
 * yet" (as opposed to a terminal declined/error), so they share the "Pending" label. Anything
 * else unrecognized intentionally falls through to null (rendered as a plain amount, same as
 * approved) rather than asserting a reason we can't back up.
 */
const walletLoadStatusLabel = (row: PayerTransactionLogEntry): "Declined" | "Failed" | "Pending" | null => {
  if (!isWalletDeposit(row)) {
    return null;
  }
  switch (row.gatewayTransactionStatus) {
    case "declined":
      return "Declined";
    case "error":
      return "Failed";
    case "pending":
    case "held_for_review":
      return "Pending";
    default:
      return null;
  }
};

/**
 * AUT-6162: an adjustment's `price` is its wallet effect (positive = the wallet went up), so its
 * sign alone decides the prefix -- a park credit is "+", a reload or permit chargeback is "-".
 */
const adjustmentRaisesBalance = (row: Pick<PayerTransactionLogEntry, "transactionType" | "price">): boolean =>
  isAdjustmentRow(row) && row.price > 0;

const amountPrefix = (row: PayerTransactionLogEntry): "+" | "-" | "" => {
  if (isAdjustmentRow(row)) {
    if (row.price > 0) {
      return "+";
    }
    return row.price < 0 ? "-" : "";
  }
  if (isWalletDeposit(row)) {
    return "+";
  }
  if (isParkRow(row) || row.gatewayTransactionType === "wallet_refund") {
    return "-";
  }
  return "";
};

/**
 * A labeled wallet load (declined/failed/pending) drops the +/- prefix -- none of those states
 * actually moved money, so asserting a direction on top of the label would be doubly misleading.
 */
const amountLabel = (row: PayerTransactionLogEntry, value: number): string => {
  const statusLabel = walletLoadStatusLabel(row);
  const formatted = `$${formatCurrency(Math.abs(value))}`;
  return statusLabel ? `${formatted} (${statusLabel})` : `${amountPrefix(row)}${formatted}`;
};

/**
 * AUT-6146: the Amount column shows the BASE amount -- what actually moved in or out of the
 * wallet -- not the full charged total. `price` includes the card service fee, so the total
 * charged to the payment method is Amount + Service Fee. The fee is subtracted from the
 * magnitude sign-correctly: Math.abs(net) = |price| - fee (same semantics as the CurbSuite
 * column, AUT-6147).
 *
 * wallet_refund is the one type whose `price` is already net of the never-refunded fee (the DB
 * view computes refund price as -(amount - service_fee_amount)), so subtracting again here would
 * double-subtract -- this mirrors the view's own wallet_refund guard. Fees are 0 on park rows,
 * non-approved (declined/pending) rows, and pre-fee history, so all of those render exactly as
 * before.
 */
export const netAmount = (
  row: Pick<PayerTransactionLogEntry, "gatewayTransactionType" | "price" | "serviceFeeAmount">,
): number => {
  const fee = Math.abs(row.serviceFeeAmount ?? 0);
  if (fee === 0 || row.gatewayTransactionType === "wallet_refund") {
    return row.price;
  }
  return Math.sign(row.price) * (Math.abs(row.price) - fee);
};

/**
 * Disputed rows are hardcoded (see disputedTransactions.ts) and always carry a negative `price`
 * (money the rider was charged), but the tab is meant to show that amount as credited back to
 * them -- hence a forced "+" prefix and a "(Disputed)" suffix instead of the usual amountLabel
 * logic (which has no notion of a disputed state).
 */
export const formatDisputedAmount = (price: number): string => `+$${formatCurrency(Math.abs(price))} (Disputed)`;

type TransactionTab = "wallet" | "permit" | "session" | "disputed";

/** Six-digit, zero-padded transaction number -- the ID column's format. */
const formatTransactionIndex = (index: number): string => index.toString().padStart(6, "0");

/** Which tab lists a gateway transaction of the given type. */
const TAB_FOR_GATEWAY_TYPE: Record<NonNullable<PayerTransactionLogEntry["gatewayTransactionType"]>, TransactionTab> = {
  initial_wallet_load: "wallet",
  wallet_reload: "wallet",
  wallet_refund: "wallet",
  payout: "wallet",
  permit: "permit",
  session_payment: "session",
};

/**
 * AUT-6162: where an adjustment's "Adjusts #…" link goes. A park (or other non-gateway usage)
 * reference has a detail page. A gateway reference (wallet_reload, permit, …) has none, so the link
 * opens this table on the tab that lists it, filtered to its ID, with the month set to the
 * reference's own month (read through the `referenced_transaction` relationship): the table always
 * applies the month range, and a chargeback often lands weeks after the charge it reverses.
 * Returns null for a non-adjustment row or one missing its reference.
 */
export const adjustmentReferenceTarget = (
  row: Pick<
    PayerTransactionLogEntry,
    | "transactionType"
    | "referenceTransactionId"
    | "referenceTransactionIndex"
    | "referenceGatewayTransactionType"
    | "referencedTransaction"
  >,
): To | null => {
  if (!isAdjustmentRow(row) || !row.referenceTransactionId) {
    return null;
  }
  if (!row.referenceGatewayTransactionType) {
    return `/transactions/${row.referenceTransactionId}`;
  }
  const referenceTimestamp = row.referencedTransaction?.transactionTimestamp;
  if (row.referenceTransactionIndex == null || !referenceTimestamp) {
    return null;
  }
  const params = filterModelToSearchParams(
    {
      items: [
        {
          id: 1,
          columnField: "transactionIndex",
          operatorValue: "equals",
          value: String(row.referenceTransactionIndex),
        },
      ],
    },
    new URLSearchParams({
      tab: TAB_FOR_GATEWAY_TYPE[row.referenceGatewayTransactionType] ?? "wallet",
      // Same format the month picker writes (startOfMonth in local time, as an ISO string).
      date: startOfMonth(new Date(referenceTimestamp)).toISOString(),
      page: "1",
      sort: DEFAULT_SORT,
    }),
  );
  return { search: `?${params.toString()}` };
};

/**
 * AUT-6162: Transaction Type cell for an adjustment -- the label, then an "Adjusts #…" link to the
 * referenced transaction followed by the reason (`transaction.description`). The full reason is also the
 * cell's tooltip, since the second line truncates.
 */
export const AdjustmentTypeCell: React.FC<{ row: PayerTransactionLogEntry }> = ({ row }) => {
  const target = adjustmentReferenceTarget(row);
  const referenceLabel =
    row.referenceTransactionIndex != null ? `Adjusts #${formatTransactionIndex(row.referenceTransactionIndex)}` : null;
  const description = row.transaction?.description ?? null;
  return (
    <Tooltip title={description ?? ""} disableHoverListener={!description}>
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.2 }}>
        <Typography variant="body1">{transactionTypeLabel(row)}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {referenceLabel &&
            (target ? (
              <Link
                component={RouterLink}
                to={target}
                variant="caption"
                // A real router link, so open-in-new-tab and copy-link get the referenced
                // transaction's address; only keep the grid from treating the click as a cell click.
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
              >
                {referenceLabel}
              </Link>
            ) : (
              referenceLabel
            ))}
          {referenceLabel && description ? " · " : ""}
          {description}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const TRANSACTION_TABS: { value: TransactionTab; label: string }[] = [
  { value: "wallet", label: "Wallet Transactions" },
  { value: "session", label: "Session Transactions" },
  { value: "permit", label: "Permit Transactions" },
];

/**
 * Kept separate from TRANSACTION_TABS rather than folded in as a fourth entry: the other three
 * tabs are always structurally present and merely filtered by paymentPreference, while this one
 * is conditionally appended only when the caller opts in via showDisputedTransactions.
 */
const DISPUTED_TRANSACTIONS_TAB: { value: TransactionTab; label: string } = {
  value: "disputed",
  label: "Disputed Transactions",
};

/**
 * AUT-6162 adjustment placement:
 * - wallet: usage fees (USAGE_FEE_FILTER), every adjustment -- each one moves the wallet balance --
 *   and wallet loads/refunds.
 * - permit: also adjustments of a permit charge, so a permit chargeback sits next to its permit.
 *   This also covers session-preference payers, who only get the session + permit tabs.
 */
export const TAB_TYPE_FILTER: Record<Exclude<TransactionTab, "disputed">, PayerTransactionsFilter> = {
  wallet: {
    _or: [
      USAGE_FEE_FILTER,
      { transaction_type: { _eq: "adjustment" } },
      { gateway_transaction_type: { _in: ["initial_wallet_load", "wallet_reload", "wallet_refund"] } },
    ],
  },
  permit: {
    _or: [
      { gateway_transaction_type: { _eq: "permit" } },
      {
        _and: [{ transaction_type: { _eq: "adjustment" } }, { reference_gateway_transaction_type: { _eq: "permit" } }],
      },
    ],
  },
  session: { gateway_transaction_type: { _eq: "session_payment" } },
};

/** Pure tab-selection logic, extracted so tests can import the real behavior instead of a copy. */
export const computeVisibleTabs = (
  paymentPreference: WalletPaymentPreference | undefined,
  showDisputedTransactions: boolean | undefined,
): { value: TransactionTab; label: string }[] => {
  const base =
    paymentPreference === "session"
      ? TRANSACTION_TABS.filter(({ value }) => value === "session" || value === "permit")
      : TRANSACTION_TABS.filter(({ value }) => value === "wallet" || value === "permit");
  return showDisputedTransactions ? [...base, DISPUTED_TRANSACTIONS_TAB] : base;
};

export interface PayerTransactionsTableProps {
  paymentPreference?: WalletPaymentPreference;
  /** AUT-5627: shows a 4th "Disputed Transactions" tab with hardcoded data. Gated by caller. */
  showDisputedTransactions?: boolean;
  /**
   * AUT-5627: whether the disputed tab's 4 hardcoded rows belong to the logged-in account. Every
   * Philly bus carrier can see the tab (showDisputedTransactions), but only one of them actually
   * owns this data — everyone else sees the tab with an empty table. Gated by caller.
   */
  showDisputedTransactionRows?: boolean;
}

/**
 * Every column's `row` is one of these. Real query rows never carry `disputed`, so a `"disputed"
 * in row` check safely narrows the union for the one column (Amount) and one column-set decision
 * (buildTableColumns) that need to tell the two apart.
 */
type PayerOrDisputedRow = PayerTransactionLogEntry | DisputedTransactionRow;

/**
 * Stable reference so the non-owner-carrier case doesn't recreate an array identity every render;
 * also the initial state before (or if never) the lazy-loaded disputed data resolves.
 */
const EMPTY_DISPUTED_ROWS: DisputedTransactionRow[] = [];

/**
 * Baseline for desktop. On narrower viewports, several of these are additionally forced hidden
 * (see `defaultVisibilityModel` below, driven by a lg=1024 breakpoint check — distinct from
 * `isIconOnly`'s md=700 breakpoint, which only collapses the toolbar's own buttons). Must stay a
 * stable, all-columns-listed constant — see useHiddenColumnsCount's docs for why.
 */
const PAYER_TRANSACTIONS_DEFAULT_VISIBILITY: GridColumnVisibilityModel = {
  transactionIndex: true,
  date: true,
  timeRange: true,
  addressStreet: true,
  city: true,
  licensePlate: true,
  transactionType: true,
  amount: true,
  serviceFee: true,
  balance: true,
  receipt: true,
  actions: true,
};

const ReceiptDownloadButton: React.FC<{ transactionId: string }> = ({ transactionId }) => {
  const [generateReceipt, { loading }] = useMutation<GenerateTransactionReceiptData, GenerateTransactionReceiptVars>(
    GENERATE_TRANSACTION_RECEIPT,
  );
  const { publishSnackbarMessage } = useSnackPackContext();

  const handleClick = async () => {
    try {
      const result = await generateReceipt({ variables: { transactionId } });
      const url = result.data?.generateTransactionReceipt?.downloadUrl;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        publishSnackbarMessage(SNACKBAR_MSGS.RECEIPT_DOWNLOAD_ERROR);
      }
    } catch {
      publishSnackbarMessage(SNACKBAR_MSGS.RECEIPT_DOWNLOAD_ERROR);
    }
  };

  return (
    <Tooltip title="Download receipt">
      <span>
        <IconButton onClick={handleClick} disabled={loading} size="small" aria-label="Download receipt">
          {loading ? <CircularProgress size={16} /> : <ReceiptLongIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

/**
 * Builds the DataGrid column list for a given tab. Extracted to module scope (rather than left
 * inline in the component's `useMemo`) so tests can assert on the real column list for the
 * disputed tab -- e.g. that `transactionType`/`receipt`/`actions` are absent -- without rendering
 * the full component. `navigate` is threaded in as a parameter instead of closed over, since this
 * function no longer lives inside the component.
 */
/**
 * AUT-6162: CSV-only columns for adjustments. Not grid columns: the Transaction Type cell already
 * shows both, standalone they'd be blank on every other row, and a new hidden-by-default grid
 * column would show up anyway for users with a saved layout (DataTableContainer uses a saved
 * visibility model as-is, so keys missing from it default to visible).
 */
const ADJUSTMENT_EXPORT_COLUMNS: GridColDef<PayerOrDisputedRow>[] = [
  {
    field: "reference",
    headerName: "Reference #",
    valueGetter: ({ row }) =>
      row.referenceTransactionIndex != null ? formatTransactionIndex(row.referenceTransactionIndex) : "",
  },
  {
    field: "description",
    headerName: "Description",
    valueGetter: ({ row }) => row.transaction?.description ?? "",
  },
];

/**
 * Columns for the CSV export (CustomExportButton drops `type: "actions"` and reuses each column's
 * valueGetter/valueFormatter): the grid's columns plus ADJUSTMENT_EXPORT_COLUMNS right after
 * Transaction Type. The disputed tab has no Transaction Type column and no adjustments.
 */
export const buildExportColumns = (
  activeTab: TransactionTab,
  navigate: NavigateFunction,
): GridColDef<PayerOrDisputedRow>[] =>
  buildTableColumns(activeTab, navigate).flatMap((column) =>
    column.field === "transactionType" ? [column, ...ADJUSTMENT_EXPORT_COLUMNS] : [column],
  );

export const buildTableColumns = (
  activeTab: TransactionTab,
  navigate: NavigateFunction,
): GridColDef<PayerOrDisputedRow>[] => {
  const baseColumns: GridColDef<PayerOrDisputedRow>[] = [
    {
      field: "transactionIndex",
      headerName: "ID",
      flex: 1,
      sortable: true,
      sortingOrder: ["desc", "asc"],
      valueGetter: ({ row }) => row.index?.toString().padStart(6, "0") ?? "-",
      filterable: true,
      filterOperators: getGridStringOperators().filter((op) => op.value === "equals"),
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      type: "date",
      sortable: true,
      sortingOrder: ["desc", "asc"],
      valueGetter: ({ row }) => new Date(row.transactionTimestamp),
      valueFormatter: ({ value }) => (value ? format(value as Date, "MM/dd/yyyy") : ""),
      filterable: true,
      filterOperators: getGridDateOperators().filter((op) => ["is", "before", "after"].includes(op.value)),
    },
    {
      field: "timeRange",
      headerName: "Session",
      flex: 1,
      sortable: false,
      filterable: false,
      valueGetter: ({ row }) =>
        isParkRow(row) ? { start: new Date(row.startTime), end: new Date(row.endTime) } : null,
      valueFormatter: ({ value }) =>
        value
          ? `${format((value as { start: Date; end: Date }).start, PARK_PERIOD_FORMAT)} - ${format(
              (value as { start: Date; end: Date }).end,
              PARK_PERIOD_FORMAT,
            )}`
          : "",
    },
    {
      field: "addressStreet",
      headerName: "Address",
      flex: 1,
      sortable: false,
      filterable: false,
      valueGetter: ({ row }) => (isParkRow(row) ? row.addressStreet || "-" : ""),
    },
    {
      field: "city",
      headerName: "Jurisdiction",
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) => (isParkRow(row) ? row.addressCity || "" : ""),
      filterable: true,
      filterOperators: getGridStringOperators().filter((op) => ["equals", "contains"].includes(op.value)),
    },
    {
      field: "licensePlate",
      headerName: "Vehicle",
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) =>
        row.licensePlateStateCode && row.licensePlateNumber
          ? `${row.licensePlateStateCode} ${row.licensePlateNumber}`
          : "-",
      filterable: true,
      filterOperators: getGridStringOperators().filter((op) => ["equals", "contains"].includes(op.value)),
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      sortable: true,
      sortingOrder: ["desc", "asc"],
      valueGetter: ({ row }) => netAmount(row),
      valueFormatter: ({ value, api, id }) => {
        const row = id != null ? (api.getRow(id) as PayerOrDisputedRow | null) : null;
        if (!row) {
          return `$${formatCurrency(Math.abs(value as number))}`;
        }
        // `price` is read here, before the `"disputed" in row` check, on purpose: some TS/tooling
        // combinations narrow `row` to `never` in a branch gated on that check -- even when the
        // check is aliased to a boolean variable first, not just written as an inline `if` -- so
        // any field access has to happen before/outside the check entirely to stay safe. `price`
        // is common to every row shape, so hoisting it here costs nothing.
        const price = row.price;
        return "disputed" in row ? formatDisputedAmount(price) : amountLabel(row, value as number);
      },
      renderCell: ({ row }) => {
        // Same hoist-before-the-check rule as valueFormatter above; netAmount only touches fields
        // common to every row shape. Disputed rows always carry a null fee, so netAmount === price
        // for them -- the disputed branch keeps reading price for clarity.
        const price = row.price;
        const net = netAmount(row);
        if ("disputed" in row) {
          return (
            <Typography variant="body1" sx={{ color: (theme: Theme) => theme.palette.success.main }}>
              {formatDisputedAmount(price)}
            </Typography>
          );
        }
        const statusLabel = walletLoadStatusLabel(row);
        const moneyIn = isWalletDeposit(row);
        const color = (theme: Theme): string => {
          if (statusLabel === "Declined" || statusLabel === "Failed") {
            return theme.palette.error.main;
          }
          if (statusLabel === "Pending") {
            return theme.palette.text.primary;
          }
          return moneyIn || adjustmentRaisesBalance(row) ? theme.palette.success.main : theme.palette.text.primary;
        };
        return (
          <Typography variant="body1" sx={{ color }}>
            {amountLabel(row, net)}
          </Typography>
        );
      },
    },
  ];

  if (activeTab === "disputed") {
    // Rows here are a fixed hardcoded array with no live query behind them, so sorting/filtering
    // would update state with no visible effect on what's shown -- these columns don't offer
    // either affordance on this tab, rather than looking active and silently doing nothing.
    return baseColumns.map((column) => ({ ...column, sortable: false, filterable: false }));
  }

  return [
    ...baseColumns,
    {
      // AUT-6146: card-payment service fee. The Amount column shows the base (fee-net) amount, so
      // the total charged to the payment method is Amount + Service Fee. Always a dollar figure,
      // never blank —
      // $0.00 for park rows, ACH/wallet-funded charges, and pre-fee history — matching the
      // CurbSuite column (AUT-6147) and keeping the CSV export's column numeric in Excel. The DB
      // view zeroes service_fee_amount for non-approved gateway rows, so declined/pending loads
      // need no special casing here; the CSV export reuses this column via its value getter.
      field: "serviceFee",
      headerName: "Service Fee",
      flex: 1,
      sortable: false,
      filterable: false,
      valueGetter: ({ row }) => Math.abs(row.serviceFeeAmount ?? 0),
      valueFormatter: ({ value }) => `$${formatCurrency(value as number)}`,
    },
    ...(activeTab === "wallet"
      ? [
          {
            // AUT-6146: running wallet balance after each row. Wallet tab only — a running balance
            // is meaningless for session/permit payment-preference payers. Can be negative, hence
            // the signed formatting (same pattern as PayeeTables/common/columns.tsx).
            field: "balance",
            headerName: "Balance",
            flex: 1,
            sortable: false,
            filterable: false,
            valueGetter: ({ row }) => row.accountBalance,
            valueFormatter: ({ value }) =>
              `${(value as number) < 0 ? "-" : ""}$${formatCurrency(Math.abs(value as number))}`,
          } as GridColDef<PayerOrDisputedRow>,
        ]
      : []),
    {
      field: "transactionType",
      headerName: "Transaction Type",
      flex: 1,
      type: "singleSelect",
      sortable: false,
      valueOptions: TRANSACTION_TYPE_OPTIONS as unknown as string[],
      valueGetter: ({ row }) => transactionTypeLabel(row),
      // Plain label for everything but adjustments; renderCell doesn't affect the CSV export.
      renderCell: ({ row, formattedValue }) =>
        isAdjustmentRow(row) ? <AdjustmentTypeCell row={row} /> : formattedValue,
      filterable: true,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === "equals")
        .map((op) => ({ ...op, InputComponent: op.InputComponent ? GridFilterInputSingleSelect : undefined })),
    },
    {
      field: "receipt",
      headerName: "",
      sortable: false,
      filterable: false,
      width: 52,
      renderCell: ({ row }) => (isParkRow(row) ? <ReceiptDownloadButton transactionId={row.transactionId} /> : null),
    } as GridColDef<PayerOrDisputedRow>,
    {
      ...ACTIONS_COLUMN_ICON,
      getActions: (params) =>
        isParkRow(params.row)
          ? [
              <GridActionsCellItem
                key="view-transaction"
                label={"View Transaction"}
                icon={<ArrowForwardIosIcon sx={ACTIONS_VIEW_ICON_SX} />}
                onClick={() => navigate(`/transactions/${params.row.transactionId}`)}
              />,
            ]
          : [],
    } as GridActionsColDef,
  ];
};

export const PayerTransactionsTable: React.FC<PayerTransactionsTableProps> = ({
  paymentPreference,
  showDisputedTransactions,
  showDisputedTransactionRows,
}) => {
  const client = useApolloClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const defaultVisibilityModel = React.useMemo<GridColumnVisibilityModel>(
    () =>
      isMobile
        ? {
            ...PAYER_TRANSACTIONS_DEFAULT_VISIBILITY,
            transactionIndex: false,
            timeRange: false,
            city: false,
            transactionType: false,
            licensePlate: false,
            serviceFee: false,
            balance: false,
          }
        : PAYER_TRANSACTIONS_DEFAULT_VISIBILITY,
    [isMobile],
  );

  const [now] = React.useState(new Date());
  const [searchParams, setSearchParams] = useSearchParams({ page: "1", sort: DEFAULT_SORT });

  const dateStr = searchParams.get("date") || startOfMonth(now).toISOString();
  const selectedDate = React.useMemo(() => new Date(dateStr), [dateStr]);
  const setSelectedDate = (newDate: Date) => {
    searchParams.set("date", newDate.toISOString());
    searchParams.set("page", "1");
    setSearchParams(searchParams, { replace: true });
  };

  const page = Number(searchParams.get("page") || 1);
  const handlePageChange = (newPage: number) => {
    const oneIndexed = newPage + 1;
    if (oneIndexed !== page) {
      searchParams.set("page", `${oneIndexed}`);
      setSearchParams(searchParams, { replace: true });
    }
  };

  const sort = searchParams.get("sort") || DEFAULT_SORT;
  const sortModel: GridSortModel = React.useMemo(() => parseSortModel(sort), [sort]);
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    searchParams.set("sort", serializeSortModel(newSortModel));
    searchParams.set("page", "1");
    setSearchParams(searchParams, { replace: true });
  };

  const orderBy: PayerTransactionOrderBy[] = React.useMemo(() => {
    const { field, sort: dir } = sortModel[0] || { field: "date", sort: "desc" };
    const direction: OrderBy = (dir as OrderBy) || "desc";
    const map: Record<string, (d: OrderBy) => PayerTransactionOrderBy> = {
      date: (d) => ({ transaction_ts: d }),
      transactionIndex: (d) => ({ index: d }),
      amount: (d) => ({ price: d }),
    };
    return [(map[field] || map.date)(direction)];
  }, [sortModel]);

  const visibleTabs = React.useMemo(
    () => computeVisibleTabs(paymentPreference, showDisputedTransactions),
    [paymentPreference, showDisputedTransactions],
  );

  const activeTabRaw = (searchParams.get("tab") || visibleTabs[0].value) as TransactionTab;
  const activeTab = visibleTabs.some(({ value }) => value === activeTabRaw) ? activeTabRaw : visibleTabs[0].value;

  const handleTabChange = (_event: React.SyntheticEvent, newTab: TransactionTab) => {
    searchParams.set("tab", newTab);
    searchParams.set("page", "1");
    // Clear column filters when switching tabs so stale filters don't cross tab boundaries.
    const next = filterModelToSearchParams({ items: [] }, searchParams);
    setSearchParams(next, { replace: true });
  };

  const appliedFilters = React.useMemo(() => parseFilterModelFromSearchParams(searchParams), [searchParams]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel | undefined>(appliedFilters);
  React.useEffect(() => {
    setFilterModel(appliedFilters);
  }, [appliedFilters]);

  const handleApplyFilters = (newFilterModel: GridFilterModel) => {
    const next = filterModelToSearchParams(newFilterModel, searchParams);
    next.set("page", "1");
    setSearchParams(next, { replace: true });
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    if (newFilterModel.items.length === 0 && filterModel?.items?.length) {
      handleApplyFilters(newFilterModel);
    }
    if (newFilterModel !== filterModel) {
      setFilterModel(newFilterModel);
    }
  };

  const where = React.useMemo(
    () => ({
      _and: [
        buildPaymentTransactionsWhere(selectedDate, appliedFilters),
        activeTab === "disputed" ? {} : TAB_TYPE_FILTER[activeTab],
      ],
    }),
    [selectedDate, appliedFilters, activeTab],
  );

  const TABLE_COLUMNS: GridColDef<PayerOrDisputedRow>[] = React.useMemo(
    () => buildTableColumns(activeTab, navigate),
    [activeTab, navigate],
  );
  const EXPORT_COLUMNS: GridColDef<PayerOrDisputedRow>[] = React.useMemo(
    () => buildExportColumns(activeTab, navigate),
    [activeTab, navigate],
  );

  const { data, loading, error, refetch } = useQuery<GetPaymentTransactionsData, GetPaymentTransactionsVars>(
    GET_PAYMENT_TRANSACTIONS,
    {
      variables: { offset: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE, orderBy, where, aggregateWhere: where },
      fetchPolicy: "network-only",
      // disputed tab renders hardcoded rows from disputedTransactions.ts; no live query needed
      skip: activeTab === "disputed",
    },
  );

  // Every Philly bus carrier can see the disputed tab, but these 4 rows belong to only one of
  // them (see disputedTransactions.ts) -- everyone else gets the tab with an empty table. The
  // `showDisputedTransactions` check here is a defensive guard, not just showDisputedTransactionRows
  // alone: the two props are independent on PayerTransactionsTableProps, so a caller could pass
  // rows=true without tab=true -- this keeps that combination from ever surfacing real data.
  //
  // disputedTransactions.ts is dynamically imported, not statically, and only when this
  // condition is true: that module holds the real hardcoded transaction data, so this is what
  // keeps it out of every other carrier's (and every other CurbPass user's) downloaded bundle --
  // their browser never issues the network request for that chunk at all.
  const isDisputedRowsOwner = showDisputedTransactionRows && showDisputedTransactions;
  const [disputedRows, setDisputedRows] = React.useState<DisputedTransactionRow[]>(EMPTY_DISPUTED_ROWS);
  const [disputedRowsLoading, setDisputedRowsLoading] = React.useState(false);

  React.useEffect(() => {
    if (!isDisputedRowsOwner) {
      return;
    }
    let cancelled = false;
    setDisputedRowsLoading(true);
    import("./disputedTransactions")
      .then(({ DISPUTED_TRANSACTIONS }) => {
        if (!cancelled) {
          setDisputedRows(DISPUTED_TRANSACTIONS);
        }
      })
      .catch(() => {
        // Chunk fetch can fail after a deploy rotates the hashed filename, or if the browser is
        // offline. Don't leave the grid spinning; an empty disputed tab degrades better than a
        // permanent loader.
      })
      .finally(() => {
        if (!cancelled) {
          setDisputedRowsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isDisputedRowsOwner]);

  const fetchTotalCount = React.useCallback(async () => {
    if (activeTab === "disputed") {
      return disputedRows.length;
    }
    const result = await client.query<GetPaymentTransactionsData, GetPaymentTransactionsVars>({
      query: GET_PAYMENT_TRANSACTIONS,
      fetchPolicy: "network-only",
      variables: { limit: 0, offset: 0, orderBy, where, aggregateWhere: where },
    });
    return result.data?.aggregate.aggregate.count ?? 0;
  }, [client, orderBy, where, activeTab, disputedRows]);

  const fetchDataBatch = React.useCallback(
    async (offset: number, limit: number) => {
      if (activeTab === "disputed") {
        return disputedRows.slice(offset, offset + limit);
      }
      const result = await client.query<GetPaymentTransactionsData, GetPaymentTransactionsVars>({
        query: GET_PAYMENT_TRANSACTIONS,
        fetchPolicy: "network-only",
        variables: { offset, limit, orderBy, where, aggregateWhere: where },
      });
      return result.data?.transactions ?? [];
    },
    [client, orderBy, where, activeTab, disputedRows],
  );

  if (error) {
    return <LocalLevelError onTryAgain={refetch} />;
  }

  const rows = activeTab === "disputed" ? disputedRows : data?.transactions || [];
  const rowCount = activeTab === "disputed" ? disputedRows.length : data?.aggregate.aggregate.count || 0;

  return (
    <DataTableContainer
      // Scoped (and remounted via key) by isMobile, not just account — mobile and desktop have
      // different defaultVisibilityModels. Without both of these, a layout saved under one
      // viewport's key would permanently win over the other viewport's default the next time
      // anything is saved, regardless of which viewport is actually active: DataTableContainer's
      // withVisibilityModelSeeded always prefers a saved columnVisibilityModel over
      // defaultVisibilityModel, with no awareness that the default here is viewport-dependent.
      key={isMobile ? "mobile" : "desktop"}
      tableName={`payer-transactions-table-${isMobile ? "mobile" : "desktop"}`}
      defaultVisibilityModel={defaultVisibilityModel}
      sx={{ height: "100%" }}
    >
      {({ apiRef, initialState, isIconOnly, resetLayout }) => (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="transaction type tabs">
              {visibleTabs.map(({ value, label }) => (
                <Tab key={value} value={value} label={label} sx={{ textTransform: "none", fontWeight: 500 }} />
              ))}
            </Tabs>
          </Box>
          <DataGridPro
            apiRef={apiRef}
            localeText={isIconOnly ? { toolbarFilters: "" } : undefined}
            getRowId={(row) => row.transactionId}
            columns={TABLE_COLUMNS}
            rows={rows}
            rowCount={rowCount}
            loading={activeTab === "disputed" ? disputedRowsLoading : loading}
            {...STANDARD_GRID_PROPS}
            pagination
            page={page - 1}
            onPageChange={handlePageChange}
            pageSize={PAGE_SIZE}
            paginationMode="server"
            rowsPerPageOptions={[PAGE_SIZE]}
            sortingMode="server"
            filterMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            components={{
              Toolbar: PayerTableToolbar,
              FilterPanel: ManualApplyGridFilterPanel,
              ColumnsPanel: FilteredColumnsPanel,
            }}
            componentsProps={{
              filterPanel: { onApplyFilters: handleApplyFilters },
              toolbar: {
                fetchTotalCount,
                fetchDataBatch,
                columns: EXPORT_COLUMNS,
                exportFileName:
                  activeTab === "disputed"
                    ? "CurbPass Disputed Transactions"
                    : `CurbPass Transactions for ${format(selectedDate, "MMMM yyyy")}`,
                batchSize: PAGE_SIZE,
                selectedDate,
                onChangeSelectedDate: setSelectedDate,
                maxDate: now,
                defaultVisibilityModel,
                isIconOnly,
                resetLayout,
                hideMonthSelector: activeTab === "disputed",
              } as PayerTableToolbarProps,
            }}
            initialState={initialState}
            sx={{
              flexGrow: 1,
              ...STANDARD_COLUMN_HEADER_TITLE_SX,
            }}
          />
        </>
      )}
    </DataTableContainer>
  );
};

export default PayerTransactionsTable;
